import React from 'react';
import styled from 'styled-components';
import solution01 from '../../assets/images/solution01_graphic.png';
import solution02 from '../../assets/images/solution02_graphic.png';
import solution03 from '../../assets/images/solution03_graphic.png';
import {
  Reveal,
  Section,
  SectionInner,
  SectionLabel,
  SectionTitle,
} from '../section/common';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const MarketingWrapper = styled(Section).attrs({ $bg: 'black' as const })`
  padding: 160px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 30px 0 0;
  }
`;

const HeaderBlock = styled.div`
  text-align: center;
  margin-bottom: 60px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 32px;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 16px;
  }
`;

const SolutionRow = styled.div<{ $delay: number; $visible: boolean }>`
  padding: 60px;
  height: 323px;
  gap: 12px;
  display: flex;
  align-items: center;
  border-radius: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 28px 24px;
    gap: 24px;
    border-radius: 28px;
    background: #fff;
  }
`;

const CardTextPanel = styled.div`
  display: flex;
  width: 100%;
  height: 323px;
  padding: 60px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  border-radius: 40px;
  background: #fff;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: auto;
    padding: 0;
    background: transparent;
    border-radius: 0;
    gap: 16px;
    justify-content: flex-start;
  }
`;

const CardTitlePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 12px;
  }
`;

const CardCategory = styled.span`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #eee;
  color: #111;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 13px;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.33px;
`;

const CardTitle = styled.h3`
  color: #111;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 36px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 43.2px */
  letter-spacing: -0.9px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 24px;
    letter-spacing: -0.6px;
  }
`;

const CardDescription = styled.p`
  color: #555;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 25.2px */
  letter-spacing: -0.45px;
  white-space: pre-line;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 15px;
    letter-spacing: -0.38px;
  }
`;

const CardGraphic = styled.img<{ $wide?: boolean }>`
  display: block;
  width: 323px;
  height: 323px;
  aspect-ratio: 1/1;
  background-color: #d3ee34;
  border-radius: 40px;
  object-fit: contain;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    border-radius: 24px;
  }
`;

const SOLUTIONS = [
  {
    category: '인플루언서 매칭',
    title: '터지는 인플루언서는\n따로 있습니다',
    description:
      '팔로워 수보다 중요한 건 사람을 움직이는 힘입니다.\n브랜드와 진짜 공감대를 만드는 인플루언서를 찾아 연결합니다.',
    graphic: solution01,
    wide: true,
  },
  {
    category: '온・오프라인 통합 캠페인',
    title: '온라인에서 시작해\n현실까지 이어집니다',
    description:
      '온라인 콘텐츠 한 편으로 끝나지 않습니다.\n온라인 확산부터 오프라인 체험까지 연결되는 캠페인을 설계합니다.',
    graphic: solution02,
    wide: false,
  },
  {
    category: '인플루언서 데이터 리포트',
    title: '감이 아닌\n데이터로 증명합니다',
    description:
      '도달과 조회수를 넘어 사람들의 행동 변화까지 분석합니다.\n캠페인의 실제 영향력을 데이터로 확인합니다.',
    graphic: solution03,
    wide: false,
  },
];

const MarketingSolutionSection: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <MarketingWrapper>
      <SectionInner ref={ref}>
        <Reveal $visible={isVisible}>
          <HeaderBlock>
            <SectionLabel>MARKETING SOLUTION</SectionLabel>
            <SectionTitle
              $light
            >{`브랜드를 터뜨리는\n가장 재미있는 방법`}</SectionTitle>
          </HeaderBlock>
        </Reveal>

        <CardList>
          {SOLUTIONS.map((item, i) => (
            <SolutionRow
              key={item.category}
              $delay={i * 0.15}
              $visible={isVisible}
            >
              <CardTextPanel>
                <CardCategory>{item.category}</CardCategory>
                <CardTitlePanel>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardTitlePanel>
              </CardTextPanel>
              <CardGraphic
                src={item.graphic}
                alt={item.category}
                $wide={item.wide}
              />
            </SolutionRow>
          ))}
        </CardList>
      </SectionInner>
    </MarketingWrapper>
  );
};

export default MarketingSolutionSection;
